import { toast } from "@shared/src/hooks/use-toast";
import { ToastActionElement } from "@shared/src/components/ui/toast";

type ToastPosition = "top-center" | "top-left" | "top-right" | "bottom-left" | "bottom-right" | "bottom-center";

type CustomToastOptions = {
  title?: string;
  description: React.ReactNode;
  variant?: "default" | "destructive" | "success";
  action?: ToastActionElement;
  position?: ToastPosition;
  permanent?: boolean;
  dismissible?: boolean;
  onOpenChange?: (open: boolean) => void;
  duration?: number;
  onClick?: () => void;
};

export const ixToast = {
  default: (message: string | React.ReactNode, options?: { title?: string; position?: ToastPosition }) =>
    toast({
      title: options?.title,
      description: message,
      ...options,
    }),
  error: (message: string | React.ReactNode | object, options?: { title?: string; position?: ToastPosition }) => {
    const errorMessage = getErrorMessage(message);

    return toast({
      title: options?.title,
      description: errorMessage,
      variant: "destructive",
      duration: 9000,
      ...options,
    });
  },
  success: (
    message: string | React.ReactNode,
    options?: { title?: string; position?: ToastPosition; onClick?: () => void },
  ) =>
    toast({
      title: options?.title,
      description: message,
      variant: "success",
      className: options?.onClick ? "cursor-pointer" : "",
      onClick: options?.onClick || undefined,
      ...options,
    }),
  warning: (message: string | React.ReactNode, options?: { title?: string; position?: ToastPosition }) =>
    toast({
      title: options?.title,
      description: message,
      variant: "default",
      className: "bg-warning",
      ...options,
    }),
  info: (message: string | React.ReactNode, options?: { title?: string; position?: ToastPosition }) =>
    toast({
      title: options?.title,
      description: message,
      ...options,
    }),
  loading: (message: string | React.ReactNode, options?: { title?: string; position?: ToastPosition }) => {
    console.log("loading toast", message);
    return toast({
      title: options?.title,
      description: message,
      variant: "default",
      duration: Infinity,
      loading: true,
      ...options,
    });
  },
  custom: (options: CustomToastOptions) => toast(options),
  dismiss: () => toast.dismiss(),
};

export const getErrorMessage = (message: string | React.ReactNode | object | Error): string => {
  let errorMessage = "";
  if (message === undefined) return "";
  try {
    if (typeof message === "string") {
      errorMessage = message;
    } else if (message instanceof Error || (typeof message === "object" && message !== null && "message" in message)) {
      errorMessage = message.message as string;
    } else {
      errorMessage = JSON.stringify(message);
    }
  } catch (e) {
    console.log("error in modal-->:", e);
    return "An error occurred. Please try again later";
  }
  console.error({ errorMessage, message });

  if (errorMessage.includes("User rejected the request")) {
    return "You rejected the request";
  }
  if (errorMessage.includes("0xe450d38c")) {
    return "Insufficient ERC20 Token Balance. Please top up your balance";
  }
  if (errorMessage.includes("ERC20InsufficientBalance")) {
    return "Insufficient Token Balance. Please top up your balance";
  }
  if (errorMessage.includes("0xfb8f41b2")) {
    return "Insufficient Allowance.";
  }
  if (errorMessage.includes("sender balance and deposit together")) {
    return "Insufficient funds for gas. Please get some gas tokens from faucet.";
  }
  if (errorMessage.includes("SignatureExpired")) {
    return "Signature expired. Please try again";
  }
  if (errorMessage.includes("Modal closed")) {
    return "";
  }
  if (errorMessage.includes("User denied transaction signature")) {
    return "You denied the transaction signature";
  }
  if (
    errorMessage.includes(
      "The total cost (gas * gas fee + value) of executing this transaction exceeds the balance of the account.",
    )
  ) {
    return "The total cost (gas * gas fee + value) of executing this transaction exceeds the balance of the account.";
  }

  return errorMessage;
};
